//  _        _      _         _
// | |  _  _| |_  _| |__ _  _| |__ _  _
// | |_| || | | || | '_ \ || | '_ \ || |
// |____\_,_|_|\_,_|_.__/\_,_|_.__/\_,_|
//
// Copyright © Lulububu Software GmbH - All Rights Reserved
// https://lulububu.de
//
// Unauthorized copying of this file, via any medium is strictly prohibited!
// Proprietary and confidential.

import React         from 'react';
import { useEffect } from 'react';

import I18n            from 'i18next';
import _               from 'lodash';
import Skeleton        from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';

import BreadCrumbBar            from '@connected/BreadCrumbBar';
import ObjectFilterRow          from '@connected/ObjectFilterRow';
import ResidentialUnitTable     from '@connected/ResidentialUnitTable';
import SearchInput              from '@connected/SearchInput';
import SearchInputType          from '@connected/SearchInput/SearchInputType';
import PagingFields             from '@constants/PagingFields';
import useContextTranslator     from '@hooks/ContextTranslator';
import { useObject }            from '@slices/object';
import Headline                 from '@stateless/atomic/Headline';
import HeadlineType             from '@stateless/atomic/Headline/HeadlineType';
import PaginationBar            from '@stateless/atomic/PaginationBar';
import Spacer                   from '@stateless/atomic/Spacer';
import CalculationNavigationBar from '@stateless/composed/CalculationNavigationBar';
import LayoutScreen             from '@stateless/composed/LayoutScreen';
import PageTitle                from '@stateless/composed/PageTitle';
import { selectObjects }        from '@store/selectors/object';
import { selectObjectPaging }   from '@store/selectors/object';
import { selectObjectLoading }  from '@store/selectors/object';

import styles from './styles.module.scss';

const CalculationObjectScreen = () => {
    const translator    = useContextTranslator('screens.calculationScreen');
    const objects       = useSelector(selectObjects);
    const objectPaging  = useSelector(selectObjectPaging);
    const dispatch      = useDispatch();
    const objectActions = useObject(dispatch);
    const objectLoading = useSelector(selectObjectLoading);

    useEffect(() => {
        objectActions.setCurrentPage();
        objectActions.removeAllObjectFilters();

        return () => {
            objectActions.removeAllObjectFilters();
        };
    }, []);

    function renderResidentialUnitTables() {
        return _.map(objects, (object) => {
            return (
                <ResidentialUnitTable
                    key={object.id}
                    data={object}
                />
            );
        });
    }

    function renderObjects() {
        if (objectLoading) {
            return _.times(5, () => <Skeleton height={58} />);
        }

        return renderResidentialUnitTables();
    }

    const handlePageClick = (event) => {
        const page = event.selected + 1;

        objectActions.setCurrentPage({
            page,
        });
    };

    return (
        <>
            <PageTitle
                title={I18n.t('calculation')}
            />
            <LayoutScreen>
                <BreadCrumbBar />
                <div className={styles.calculationObjectScreen}>
                    <div className={styles.headerNavigationRow}>
                        <Headline
                            type={HeadlineType.headline2}
                            title={translator.t('calculationObjectTitle')}
                        />
                        <CalculationNavigationBar />
                    </div>
                    <Spacer height={10} />
                    <ObjectFilterRow />
                    <SearchInput type={SearchInputType.object} />
                    <div className={styles.objects}>
                        {renderObjects()}
                    </div>
                    <PaginationBar
                        initialPage={objectPaging[PagingFields.currentPage] - 1}
                        onPageChange={handlePageClick}
                        pageRangeDisplayed={1}
                        pageCount={objectPaging[PagingFields.lastPage]}
                    />
                    <CalculationNavigationBar />
                </div>
            </LayoutScreen>
        </>
    );
};

export default CalculationObjectScreen;
